import { Plus, Search, Filter, Eye, EyeOff, Edit, Trash2, Package } from 'lucide-react';
import { motion } from 'framer-motion';

export const ProductsTab = ({
  isArabic,
  products,
  categories,
  filterCategory,
  setFilterCategory,
  searchQuery,
  setSearchQuery,
  onAddProduct,
  onEditProduct,
  onDeleteProduct,
  onToggleVisibility,
  currentPage,
  totalPages,
  totalProducts,
  onPageChange
}) => {
  // Filter products
  const filteredProducts = products.filter(p => {
    const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
    const matchesSearch = !searchQuery || 
      p.nameAr?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.nameEn?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900">
          {isArabic ? 'إدارة المنتجات' : 'Manage Products'}
        </h2>
        <button
          onClick={onAddProduct}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-xl hover:bg-slate-800 transition-colors"
          data-testid="add-product-btn"
        >
          <Plus className="w-5 h-5" />
          {isArabic ? 'إضافة منتج' : 'Add Product'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder={isArabic ? 'بحث...' : 'Search...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-4 pr-12 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent"
            data-testid="search-input"
          />
        </div>
        <div className="relative">
          <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="pl-4 pr-12 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black focus:border-transparent appearance-none bg-white min-w-[200px]"
            data-testid="category-filter"
          >
            <option value="all">{isArabic ? 'جميع الفئات' : 'All Categories'}</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {isArabic ? cat.nameAr : cat.nameEn}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredProducts.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            categories={categories}
            isArabic={isArabic}
            onEdit={() => onEditProduct(product)}
            onDelete={() => onDeleteProduct(product.id)}
            onToggleVisibility={() => onToggleVisibility(product.id)}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            {isArabic ? 'لا توجد منتجات' : 'No products found'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalProducts={totalProducts}
          visibleCount={filteredProducts.length}
          onPageChange={onPageChange}
          isArabic={isArabic}
        />
      )}
    </div>
  );
};

const ProductCard = ({ product, categories, isArabic, onEdit, onDelete, onToggleVisibility }) => {
  const category = categories.find(c => c.id === product.category);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow ${
        !product.isVisible ? 'opacity-60' : ''
      }`}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="aspect-square bg-slate-100 relative">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.nameAr}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-slate-300" />
          </div>
        )}
        {!product.isVisible && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <EyeOff className="w-8 h-8 text-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-xs text-slate-500 mb-1">
          {isArabic ? category?.nameAr : category?.nameEn}
        </p>
        <h3 className="font-semibold text-slate-900 mb-3 line-clamp-2">
          {isArabic ? product.nameAr : product.nameEn}
        </h3>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onToggleVisibility}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              product.isVisible
                ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                : 'bg-green-100 text-green-600 hover:bg-green-200'
            }`}
            data-testid={`toggle-visibility-${product.id}`}
          >
            {product.isVisible ? (
              <><EyeOff className="w-4 h-4 inline mr-1" />{isArabic ? 'إخفاء' : 'Hide'}</>
            ) : (
              <><Eye className="w-4 h-4 inline mr-1" />{isArabic ? 'إظهار' : 'Show'}</>
            )}
          </button>
          <button
            onClick={onEdit}
            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
            data-testid={`edit-product-${product.id}`}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
            data-testid={`delete-product-${product.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const Pagination = ({ currentPage, totalPages, totalProducts, visibleCount, onPageChange, isArabic }) => (
  <>
    <div className="flex items-center justify-center gap-2 mt-8 pb-4" data-testid="pagination">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-4 py-2 rounded-lg ${
          currentPage === 1
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
        }`}
      >
        {isArabic ? 'السابق' : 'Previous'}
      </button>

      <div className="flex items-center gap-1">
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum;
          if (totalPages <= 5) pageNum = i + 1;
          else if (currentPage <= 3) pageNum = i + 1;
          else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = currentPage - 2 + i;
          
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`w-10 h-10 rounded-lg font-medium ${
                currentPage === pageNum
                  ? 'bg-black text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-4 py-2 rounded-lg ${
          currentPage === totalPages
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
        }`}
      >
        {isArabic ? 'التالي' : 'Next'}
      </button>
    </div>

    <div className="text-center text-sm text-slate-500 mt-2">
      {isArabic 
        ? `عرض ${visibleCount} من ${totalProducts} منتج`
        : `Showing ${visibleCount} of ${totalProducts} products`}
    </div>
  </>
);

export default ProductsTab;
